import React, { useState, useEffect } from 'react';
import { useNotification } from 'src/hooks/useNotification';
import AddButtonIcon from '../../assets/img/add-button-icon.svg';
import Input from 'src/components/Input/Input';
import { useForm, Controller } from 'react-hook-form';
import { useLoader } from 'src/provider/LoaderProvider';
import { useGetUsersMeQuery, useCreateUserMutation } from 'src/services/usersService';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  emailScheme,
  stringScheme,
  passwordScheme,
  passwordConfirmScheme,
} from 'src/utils/validation-schemes';
import { object } from 'yup';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { baseApi, useAddProjectTeamMembersMutation, useGetAllProjectsQuery } from 'src/services';
import { ProjectsMultiSelect } from 'src/components/ProjectsMultiSelect/ProjectsMultiSelect';
import { ROLES, ROLES_PRETTY } from 'src/constants/roles';
import { ProductsOrder, ProductsTypeBack } from 'src/constants/products';
import { useAppDispatch } from 'src/store';
import { Button } from 'src/components/ui/button';
import type { ProjectOption } from 'src/components/ProjectsMultiSelect/ProjectsMultiSelect';
import { UserNameFields, UserPasswordFields } from 'src/components/UserFormFields/UserFormFields';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';

export const AddUserPopup = ({ disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const dispatch = useAppDispatch();
  const notification = useNotification();
  const [roles, setRoles] = useState([
    { label: ROLES_PRETTY.admin, value: ROLES.ADMIN },
    { label: ROLES_PRETTY.manager, value: ROLES.MANAGER },
    { label: ROLES_PRETTY.support_team_lead, value: ROLES.SUPPORT_TEAM_LEAD },
    { label: ROLES_PRETTY.support, value: ROLES.SUPPORT },
  ]);
  const [sortedProjects, setSortedProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState<ProjectOption[]>([]);
  const [addTeamMember, { isLoading: isLoadingAddTeamMember, isSuccess: isSuccessAddTeamMember }] =
    useAddProjectTeamMembersMutation();
  const [
    createUser,
    { data: createUserData, isSuccess: isSuccessCreateUser, isLoading: isLoadingCreateUser },
  ] = useCreateUserMutation();
  const { data: userInfo } = useGetUsersMeQuery();
  const { data: projects = [] } = useGetAllProjectsQuery();

  const { handleSubmit, control, trigger, reset, setError, clearErrors } = useForm({
    defaultValues: {
      email: '',
      first_name: '',
      last_name: '',
      password: '',
      password2: '',
      role: {
        label: '',
      },
    },
    resolver: yupResolver(
      object({
        email: emailScheme({ required: true }).label('Email'),
        first_name: stringScheme({ required: true }).label('First name'),
        last_name: stringScheme().label('Last name'),
        password: passwordScheme({
          required: true,
          min: 8,
          max: 50,
          minMessage: 'Min. 8 characters',
        }).label('Password'),
        password2: passwordConfirmScheme({
          required: true,
          min: 8,
          max: 50,
          minMessage: 'Min. 8 characters',
        }).label('Confirm password'),
        role: object({
          label: stringScheme({ required: true }),
        }).typeError('Role is required'),
      }),
    ),
  });
  const loader = useLoader();

  useEffect(() => {
    if (projects?.length) {
      const clone = [...projects];

      clone.sort((a, b) => {
        return ProductsOrder[a.project_type] - ProductsOrder[b.project_type];
      });

      setSortedProjects(clone);
    }
  }, [projects]);

  useEffect(() => {
    if (isSuccessAddTeamMember) {
      notification.success(`Team Member was added.`);
    }
  }, [isSuccessAddTeamMember]);

  useLoaderWhile(isLoadingAddTeamMember);

  useEffect(() => {
    (async () => {
      if (isSuccessCreateUser) {
        notification.success(`New user ${createUserData.first_name} was added.`);

        if (selectedProjects.length) {
          for (const selectedProject of selectedProjects) {
            await addTeamMember({
              id: selectedProject.id,
              users: [createUserData.id],
              type: ProductsTypeBack[selectedProject.project_type],
            });
          }
        }
      }

      dispatch(baseApi.util.invalidateTags(['UsersAll']));
      reset();
      setOpen(false);
    })();
  }, [isSuccessCreateUser]);

  useLoaderWhile(isLoadingCreateUser);

  useEffect(() => {
    if (userInfo?.role !== ROLES.COSSUPORT_MANAGER) {
      setRoles(roles.filter((role) => role.value !== ROLES.COSSUPORT_MANAGER));
    }
  }, [userInfo]);

  const submit = async (data: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    password2: string;
    role: { label: string; value?: string };
  }) => {
    const isCompanyEmail = await fetch(`/api/is-company-email?email=${data.email}`).then((res) =>
      res.json(),
    );

    if (isCompanyEmail && !isCompanyEmail.result) {
      setError('email', {
        message: 'Invalid',
      });
      setEmailErrorMessage('Please use the corporate email.');

      return loader.hide();
    }

    const validationResponse = await fetch(`/api/mailgun-validate?email=${data.email}`).then(
      (res) => res.json(),
    );

    if (validationResponse?.result && !validationResponse.result.isValid) {
      setError('email', { message: 'Invalid' });
      setEmailErrorMessage(validationResponse?.result?.message || '');

      return loader.hide();
    }

    const request = {
      email: data.email,
      password: data.password,
      password2: data.password2,
      first_name: data.first_name,
      last_name: data.last_name,
      role: data.role.value || ROLES.SUPPORT,
    };

    createUser(request);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
      >
        <Button variant="pill-outline" size="pill" className="card-header-button">
          <span>Add user</span>
          <img src={AddButtonIcon} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="content">
            <form id="add-user-form" onSubmit={handleSubmit(submit)}>
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <div className="relative">
                      <Input
                        type={'email'}
                        label={'Email'}
                        placeholder={'email@email.com'}
                        invalidMessage={error?.message}
                        value={value}
                        isValid={!error?.message}
                        onChange={onChange}
                        onBlur={() => trigger('email')}
                        onFocus={() => {
                          clearErrors('email');
                          setEmailErrorMessage('');
                        }}
                      />
                      {Boolean(emailErrorMessage) && (
                        <div
                          className={
                            'absolute top-[calc(100%+4px)] w-full bg-error_bg text-error text-center text-sm font-semibold font-jamjuree py-1 px-4 z-10 rounded-sm-5 shadow'
                          }
                        >
                          {emailErrorMessage}
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
              <UserNameFields control={control} trigger={trigger} />
              <div className={'input-container'}>
                <ProjectsMultiSelect
                  label={'Project'}
                  projects={sortedProjects}
                  onChange={(selected) => {
                    setSelectedProjects(selected);
                  }}
                />
              </div>
              <UserPasswordFields control={control} />
              <div className={'input-container'}>
                <Controller
                  control={control}
                  name="role"
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Input
                      type={'select'}
                      label={'Role'}
                      placeholder={'Admin'}
                      invalidMessage={error?.message}
                      value={value}
                      selectOptions={roles}
                      isValid={!error?.message}
                      onChange={onChange}
                    />
                  )}
                />
              </div>
              <div className={'input-container buttons'}>
                <Button variant="pill-green" size="pill" type="submit" form="add-user-form">
                  Add User
                </Button>
              </div>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
